import { api } from '../axios';
import type { 
   
  UserFilters, 
  UserListResponse, 
  UserResponse, 
  UserStatsResponse 
} from '@/types/user';
import type { UserLookup, UserLookupPurpose } from '@/types/user';

function parseLookup(rows: unknown): UserLookup[] {
  if (!Array.isArray(rows)) throw new Error('Invalid user lookup response');
  return rows.map(row => {
    if (!row || typeof row !== 'object' || typeof row.actor_id !== 'string' || !row.actor_id ||
      typeof row.full_name !== 'string' || typeof row.role !== 'string' || !row.role) throw new Error('Invalid user lookup response');
    return { actor_id: row.actor_id, full_name: row.full_name, role: row.role };
  });
}

// User Profile API
export const userAPI = {
  lookupUsers: async (purpose: UserLookupPurpose, search = '', limit = 20): Promise<UserLookup[]> => {
    const response = await api.get('/users/lookup', { params: { purpose, search, limit } });
    return parseLookup(response.data.data);
  },

  lookupUser: async (purpose: UserLookupPurpose, actorID: string): Promise<UserLookup | null> => {
    if (!actorID || new TextEncoder().encode(actorID).length > 256) throw new Error('Invalid user lookup request');
    const response = await api.get('/users/lookup', { params: { purpose, actor_id: actorID } });
    const rows = parseLookup(response.data.data);
    if (rows.length > 1 || rows.length === 1 && rows[0].actor_id !== actorID) throw new Error('Invalid user lookup response');
    return rows[0] || null;
  },

  // Get current user profile
  getCurrentUser: async (): Promise<UserResponse> => {
    const response = await api.get('/user/me');
    return response.data;
  },

  // Admin functions
  listUsers: async (filters?: UserFilters): Promise<UserListResponse> => {
    const params = new URLSearchParams();
    
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('page_size', filters.limit.toString());
    if (filters?.sort_by) params.append('sort_by', filters.sort_by);
    if (filters?.sort_desc) params.append('sort_order', 'desc');

    const response = await api.get(`/users?${params.toString()}`);
    return response.data;
  },

  getUserById: async (userId: string): Promise<UserResponse> => {
    const response = await api.get(`/users/${userId}`);
    return response.data;
  },

  getUserStats: async (): Promise<UserStatsResponse> => {
    const response = await api.get('/users/stats');
    return response.data;
  },

};

// Export individual functions for easier importing
export const {
  getCurrentUser,
  listUsers,
  getUserById,
  getUserStats,
} = userAPI;
